import { httpRequest } from "@services/http";

const BASE = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/sequence/`;

export interface SequenceGroupOut {
  id: number;
  name: string;
  note: string;
  position: number;
}

/*
    One touch as this campaign will send it, with its overrides already laid over the
    catalog by the server. The client never merges these itself: the resolver is the single
    place that decides, so a preview here cannot drift from what actually sends.
*/
export interface ResolvedTouchOut {
  touch_id: number | null;
  override_id: number | null;
  ad_id: number;
  channel: "email" | "sms";
  title: string;
  position: number;
  offset_days: number;
  direction: "before" | "after" | "on";
  key_date_type_id: number | null;
  send_hour: number;
  is_on: boolean;
  subject: string;
  preheader: string;
  html: string;
  text: string;
  group_id: number | null;
  is_added: boolean;
  is_customized: boolean;
  trigger_overridden: boolean;
  copy_overridden: boolean;
  catalog_updated: boolean;
  /** This touch's own approval row — a touch is approved or returned individually. */
  launch_ad_id?: number | null;
  launch_ad_status?: string | null;
  review_comment?: string;
}

export interface LaunchSequenceOut {
  /** Null until this campaign has been configured — the server writes no row on a read. */
  id: number | null;
  sequence_id: number;
  sequence_name: string;
  sequence_status: "draft" | "published";
  stage: string | null;
  target_action: string;
  window: string;
  is_on: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  audience_mode: "campaign" | "separate";
  audience_id: number | null;
  groups: SequenceGroupOut[];
  /** Touches the playbook holds per channel, regardless of this campaign's switches. */
  email_touch_count?: number;
  sms_touch_count?: number;
  touches: ResolvedTouchOut[];
}

export interface CatalogTouchOut {
  id: number;
  ad_id: number;
  channel: "email" | "sms";
  title: string;
  position: number;
  offset_days: number;
  direction: "before" | "after" | "on";
  key_date_type_id: number;
  key_date_code: string;
  send_hour: number;
  group_id: number | null;
  subject: string;
  preheader: string;
  html: string;
  text: string;
  version: number;
}

export interface SequenceOut {
  id: number;
  name: string;
  stage: string | null;
  target_action: string;
  window: string;
  status: "draft" | "published";
  organization_id: number | null;
  starter_template_id: number | null;
  touch_count: number;
  /** Campaigns with this playbook switched on, counted server-side (MPN-2201). */
  used_in: number;
  groups: SequenceGroupOut[];
}

export interface SequenceDetailOut extends SequenceOut {
  touches: CatalogTouchOut[];
}

export interface LaunchSequencePatch {
  is_on?: boolean;
  email_enabled?: boolean;
  sms_enabled?: boolean;
  audience_mode?: "campaign" | "separate";
  audience_id?: number | null;
}

/*
    Every field is optional, and sending one as null stops overriding it — that is how a
    per-field reset works. Resetting a whole touch calls `resetTouch` instead.
*/
export interface TouchOverridePatch {
  is_on?: boolean | null;
  offset_days?: number | null;
  direction?: string | null;
  key_date_type_id?: number | null;
  send_hour?: number | null;
  subject?: string | null;
  preheader?: string | null;
  html?: string | null;
  cta_label?: string | null;
  sms_text?: string | null;
}

/** Mirrors the four states the footer shows. `changes` is the client's own answer. */
export type ApprovalStateOut = "not_sent" | "awaiting" | "approved" | "changes";

export interface PlaybookApprovalOut {
  sequence_id: number;
  sequence_name: string;
  state: ApprovalStateOut;
  touch_count: number;
}

/** Who a campaign's messages come from, resolved server-side (MPN-2201).

    Three values from three places the browser cannot reach: the campaign's email row, the
    platform's sending number, and the CAN-SPAM address appended at send time. Blank where
    nothing is set -- a preview that shows no From address is the honest answer. */
export interface SenderIdentity {
  from_name: string;
  from_address: string;
  sms_sender: string;
  footer_address: string;
  /**
   * Fictional sample values for a catalog preview, keyed by the registry's bracketed
   * spelling. Absent on a campaign's own sender identity, which renders against real values.
   * A token with no sample previews as its bracket, marked amber.
   */
  sample_values?: Record<string, string>;
}

export interface SequenceStage {
  id: number;
  name: string;
}

export interface CatalogTouchPatch {
  title?: string;
  subject?: string;
  preheader?: string;
  html?: string;
  text?: string;
}

/** One touch on the Schedule page's calendar (MPN-2210). Dates are the scheduler's own. */
export interface PlannedTouch {
  launch_id: number;
  sequence_id: number;
  sequence_name: string;
  ad_id: number;
  channel: string;
  title: string;
  position: number;
  /** Null when the touch's key date is not set on this campaign, so it has no place to sit. */
  send_date: string | null;
  /** Why it will not go, in the words the screen shows. Null means it will. */
  blocked: string | null;
}

export interface EngagementKeyDate {
  launch_id: number;
  code: string;
  label: string;
  date: string;
}

export interface EngagementScheduleOut {
  touches: PlannedTouch[];
  key_dates: EngagementKeyDate[];
}

export interface ApprovalOut {
  state: ApprovalStateOut;
  touch_count: number;
  sent?: number | null;
  /** Who answered the review, and when, for the banners that name them (MPN-2207). */
  reviewer_name?: string | null;
  reviewed_at?: string | null;
  /** One row per switched-on playbook — each goes to the client as its own request. */
  playbooks?: PlaybookApprovalOut[];
}

/** "good" | "change" | "" while the client has not decided. */
export type TouchVerdict = "good" | "change" | "";

export interface ReviewTouchOut {
  ad_id: number;
  channel: string;
  title: string;
  position: number;
  subject?: string;
  preheader?: string;
  html?: string;
  text?: string;
  verdict: TouchVerdict;
  comment?: string;
}

export interface ReviewProgressOut {
  total: number;
  reviewed: number;
  good: number;
  needs_change: number;
  can_submit: boolean;
}

export interface ReviewOut {
  sequence_id: number;
  sequence_name: string;
  state: ApprovalStateOut;
  progress: ReviewProgressOut;
  touches: ReviewTouchOut[];
}

export class SequenceService {
  /** Every published playbook, with this campaign's decisions already applied. */
  /* Named for the model, not the screen. The server calls this a LaunchSequence; the Email
     & SMS tab calls it a campaign's playbook, and both are right -- see the Ubiquitous
     Language page. The service layer mirrors the API, the component layer speaks Ops. */
  async getLaunchSequences(launchId: number) {
    return await httpRequest<LaunchSequenceOut[]>(
      `${BASE}launches/${launchId}/sequences/`,
      { method: "GET" },
    );
  }

  async configureSequence(
    launchId: number,
    sequenceId: number,
    payload: LaunchSequencePatch,
  ) {
    return await httpRequest<LaunchSequenceOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/`,
      { method: "PUT", body: JSON.stringify(payload) },
    );
  }

  async overrideTouch(
    launchId: number,
    sequenceId: number,
    touchId: number,
    payload: TouchOverridePatch,
  ) {
    return await httpRequest<ResolvedTouchOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/touches/${touchId}/`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
  }

  /** Back to the catalog default, by dropping the override row entirely. */
  async resetTouch(launchId: number, sequenceId: number, touchId: number) {
    await httpRequest(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/touches/${touchId}/override/`,
      // 204, no body. httpRequest already tolerates an empty response.
      { method: "DELETE" },
    );
  }

  /** Only a step this campaign added. A catalog touch is reset, never removed. */
  async deleteAddedStep(launchId: number, sequenceId: number, overrideId: number) {
    await httpRequest(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/added/${overrideId}/`,
      // 204, no body. httpRequest already tolerates an empty response.
      { method: "DELETE" },
    );
  }

  /** Footer state for this campaign's sequences (MPN-2207). */
  async getApproval(launchId: number) {
    return await httpRequest<ApprovalOut>(
      `${BASE}launches/${launchId}/sequences-approval/`,
      { method: "GET" },
    );
  }

  /** Send one playbook as its own request. Also the resubmit: approved touches stay put. */
  async sendPlaybookForApproval(launchId: number, sequenceId: number) {
    return await httpRequest<ApprovalOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/approval/`,
      { method: "POST" },
    );
  }

  /** The playbook as the client reviews it. */
  async getReview(launchId: number, sequenceId: number) {
    return await httpRequest<ReviewOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/review/`,
      { method: "GET" },
    );
  }

  /** Good to go, or Needs a change — which requires a comment. */
  async decideTouch(
    launchId: number,
    sequenceId: number,
    adId: number,
    approved: boolean,
    comment?: string,
  ) {
    return await httpRequest<ReviewOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/review/touches/${adId}/`,
      { method: "POST", body: JSON.stringify({ approved, comment }) },
    );
  }

  /** Approve the undecided touches. Never overrides a Needs a change. */
  async approveRemaining(launchId: number, sequenceId: number) {
    return await httpRequest<ReviewOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/review/approve-remaining/`,
      { method: "POST" },
    );
  }

  /** Hand the playbook back to Ops. Refused until every touch is reviewed. */
  async submitReview(launchId: number, sequenceId: number) {
    return await httpRequest<ReviewOut>(
      `${BASE}launches/${launchId}/sequences/${sequenceId}/review/submit/`,
      { method: "POST" },
    );
  }

  /** The real values behind this campaign's tokens, for the client preview. */
  async getTokenValues(launchId: number) {
    return await httpRequest<Record<string, string>>(
      `${BASE}launches/${launchId}/token-values/`,
      { method: "GET" },
    );
  }

  async listCatalogSequences(status?: string) {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    return await httpRequest<SequenceOut[]>(`${BASE}sequences/${query}`, { method: "GET" });
  }

  async getCatalogSequence(sequenceId: number) {
    return await httpRequest<SequenceDetailOut>(`${BASE}sequences/${sequenceId}/`, {
      method: "GET",
    });
  }

  /** Who this campaign's messages come from. See SenderIdentity. */
  async getSenderIdentity(launchId: number) {
    return await httpRequest<SenderIdentity>(`${BASE}launches/${launchId}/sender-identity/`, {
      method: "GET",
    });
  }

  /** The funnel stages a playbook can be filed under, for the catalog's filter. */
  async listSequenceStages() {
    return await httpRequest<SequenceStage[]>(`${BASE}sequence-stages/`, { method: "GET" });
  }

  /** The fictional sender a catalog preview renders as. See PreviewIdentity. */
  async getCatalogPreviewIdentity() {
    return await httpRequest<SenderIdentity>(`${BASE}catalog-preview-identity/`, {
      method: "GET",
    });
  }

  /** Rewrite one catalog message. Reaches every campaign that has not overridden it. */
  async editCatalogTouch(sequenceId: number, touchId: number, patch: CatalogTouchPatch) {
    return await httpRequest<CatalogTouchOut>(
      `${BASE}sequences/${sequenceId}/touches/${touchId}/`,
      { method: "PATCH", body: patch },
    );
  }

  /** Make a playbook selectable on campaigns, or take it back to draft. */
  async setCatalogSequencePublished(sequenceId: number, published: boolean) {
    return await httpRequest<SequenceOut>(
      `${BASE}sequences/${sequenceId}/${published ? "publish" : "unpublish"}/`,
      { method: "POST" },
    );
  }

  /** Every campaign's Email & SMS sends, for the Schedule page (MPN-2210). */
  async getEngagementSequenceSchedule(engagementId: number) {
    return await httpRequest<EngagementScheduleOut>(
      `${BASE}engagements/${engagementId}/sequence-schedule/`,
      { method: "GET" },
    );
  }
}
